/**
 * useRepositoryCommit Hook
 * Provides commit/pull/discard operations for repository-bound shapes
 *
 * This hook manages the commit-based workflow:
 * - Commit: Push local changes to the external repository (with pre-commit conflict detection)
 * - Pull: Fetch latest data and detect conflicts
 * - Discard: Revert local changes to last committed state
 *
 * Edge cases handled:
 * - Deleted externally: Record no longer exists → shape marked with RECORD_NOT_FOUND error
 * - Changed externally: Field-level conflict detection (same field changed both sides)
 * - Renewed externally: All fields replaced → detected via field-level comparison
 * - New record commit: isNewRecord shapes skip pull (no external record to compare)
 */

import { useCallback, useState } from 'react';
import type * as Y from 'yjs';
import type { ShapeDataBinding, ISODateString } from '../models/ShapeDataBinding';
import { readYjsValue } from '../../../lib/diagram/yjsCompat';
import { ShapeMapAdapter } from '../../../lib/diagram/ShapeMapAdapter';
import { ShapePersistenceGateway } from '../../../lib/diagram/shapeGeometry';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import {
  markBindingCommitted,
  discardLocalChanges,
  hasUncommittedChanges,
  getEffectiveRecord,
  toISODateString,
} from '../models/ShapeDataBinding';
import { isNewRecordBinding } from '../utils/createNewRecordBinding';
import {
  discardAllBoundRecordChanges,
  getBoundRecordState,
  markRecordsAsCommitted,
} from '../stores/BoundRecordStore';
import {
  detectFieldsFromProfile,
  type DomainDataProfile,
  type EdgeTableRelationshipMapping,
} from '../../../core/data-profiles';
import { reverseFieldMapping } from '../utils/reverseFieldMapping';
import type { FieldMapping, FieldTransform } from '../models/DiagramDataSourceConnection';
import {
  getBoundEdges as getBoundEdgesFromDoc,
  getDeletedEdgeBindings,
  clearDeletedEdgeBindings,
} from '../utils/edgeBinding';
import {
  getDeletedNodeBindings,
  clearDeletedNodeBindings,
} from '../utils/nodeBinding';
import { useQueryClient } from '@tanstack/react-query';
import { dataSourceKeys } from './useDataSources';
import { getNodeMutationManager } from '../../../core/commands/mutations/NodeMutationManager';
import type { Edge } from '../../../core/models/scene';
import type { SourceAuthorityType } from '../../../core/data-profiles/sourceAuthorityTypes';
import { buildShapeUnbindPatch } from '../utils/shapeUnbindPatch';

export interface CommitResult {
  success: boolean;
  committed: number;
  failed: number;
  conflicts: ConflictInfo[];
  deletions: DeletionInfo[];
  errors: Array<{ shapeId: string; error: string }>;
}

export interface PullResult {
  success: boolean;
  updated: number;
  conflicts: ConflictInfo[];
  deletions: DeletionInfo[];
  errors: Array<{ shapeId: string; error: string }>;
}

export interface ConflictInfo {
  shapeId: string;
  recordId: string;
  localChanges: Record<string, unknown>;
  remoteChanges: Record<string, unknown>;
  baseRecord: Record<string, unknown>;
}

export interface DeletionInfo {
  shapeId: string;
  recordId: string;
  hadLocalChanges: boolean;
  lastKnownData: Record<string, unknown>;
}

export interface DiscardResult {
  discarded: number;
  errors: string[];
}

interface UseRepositoryCommitOptions {
  doc: Y.Doc | null;
  sourceId: string | undefined;
  diagramId: string | undefined;
  /** Enables durable change-set write-back instead of the legacy direct commit route. */
  folderId?: string;
  sourceType?: SourceAuthorityType;
  domain?: string;
  profile?: DomainDataProfile<any, any, any, any>;
  fieldMappings?: FieldMapping[];
  onCommitSuccess?: (result: CommitResult) => void;
  onPullSuccess?: (result: PullResult) => void;
  onConflictsDetected?: (conflicts: ConflictInfo[]) => void;
}

/**
 * Hook for managing repository commit operations
 */
export function useRepositoryCommit(options: UseRepositoryCommitOptions) {
  const {
    doc,
    sourceId,
    profile,
    fieldMappings = [],
    folderId,
    sourceType,
    domain,
    onCommitSuccess,
    onPullSuccess,
    onConflictsDetected,
  } = options;
  const queryClient = useQueryClient();
  const authenticatedFetch = useAuthenticatedFetch();

  const [isCommitting, setIsCommitting] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [lastCommitResult, setLastCommitResult] = useState<CommitResult | null>(null);
  const [lastPullResult, setLastPullResult] = useState<PullResult | null>(null);

  const normalizeManagerId = useCallback((value: unknown): string | null => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }, []);

  const getManagerFieldValue = useCallback(
    (data: Record<string, unknown> | undefined): string | null => {
      if (!data) return null;
      return (
        normalizeManagerId(data.reportsTo) ??
        normalizeManagerId(data.reports_to) ??
        normalizeManagerId(data.managerId) ??
        normalizeManagerId(data.manager_id)
      );
    },
    [normalizeManagerId]
  );

  const getShapeBinding = useCallback((shapeRecord: Record<string, unknown>): ShapeDataBinding | undefined => {
    const props = shapeRecord.props as Record<string, unknown> | undefined;
    const attrs = shapeRecord.attrs as Record<string, unknown> | undefined;
    const propsBinding = props?.dataBinding as ShapeDataBinding | undefined;
    if (propsBinding?.sourceId && propsBinding?.recordId) return propsBinding;

    const attrsBinding = attrs?.dataBinding as ShapeDataBinding | undefined;
    if (attrsBinding?.sourceId && attrsBinding?.recordId) return attrsBinding;

    const topLevelBinding = shapeRecord.dataBinding as ShapeDataBinding | undefined;
    if (topLevelBinding?.sourceId && topLevelBinding?.recordId) return topLevelBinding;

    return undefined;
  }, []);

  const getShapeRecordKeys = useCallback((shape: Record<string, unknown>, binding?: ShapeDataBinding): string[] => {
    const keys = new Set<string>();
    const pushKey = (value: unknown) => {
      if (typeof value !== 'string') return;
      const trimmed = value.trim();
      if (trimmed.length > 0) keys.add(trimmed);
    };

    pushKey(binding?.recordId);
    pushKey(shape.id);

    const attrs = shape.attrs as Record<string, unknown> | undefined;
    const props = shape.props as Record<string, unknown> | undefined;
    pushKey(attrs?.personId);
    pushKey(attrs?.id);
    pushKey(props?.personId);
    pushKey(props?.id);

    return Array.from(keys);
  }, []);

  const getPrimaryShapeRecordKey = useCallback(
    (shape: Record<string, unknown>, binding?: ShapeDataBinding): string | null => {
      const keys = getShapeRecordKeys(shape, binding);
      return keys.length > 0 ? keys[0] : null;
    },
    [getShapeRecordKeys]
  );

  const getReportingManagerByRecordId = useCallback((): Map<string, string> => {
    const result = new Map<string, string>();
    if (!doc || !sourceId) return result;

    const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));
    const bindingByShapeId = new Map<string, ShapeDataBinding>();
    const shapeById = new Map<string, Record<string, unknown>>();
    shapesMap.forEach((rawShape, shapeId) => {
      const shape = readYjsValue<Record<string, unknown>>(rawShape);
      shapeById.set(shapeId, shape);
      const binding = getShapeBinding(shape);
      if (binding?.sourceId === sourceId) {
        bindingByShapeId.set(shapeId, binding);
      }
    });

    const connectorsMap = doc.getMap('connectors');
    connectorsMap.forEach((rawConnector) => {
      const connector = readYjsValue<Record<string, unknown>>(rawConnector);
      const edgeType = String(connector.type ?? '');
      const attrs = (connector.attrs as Record<string, unknown> | undefined) ?? {};
      const relation = String(attrs.relation ?? '');
      const isReporting =
        edgeType === 'reporting' ||
        edgeType === 'reports_to' ||
        relation === 'reporting' ||
        relation === 'reports_to';
      if (!isReporting) return;

      const fromShapeId = String(connector.from ?? '');
      const toShapeId = String(connector.to ?? '');
      if (!fromShapeId || !toShapeId) return;

      const fromShape = shapeById.get(fromShapeId);
      const toShape = shapeById.get(toShapeId);
      if (!fromShape || !toShape) return;

      const fromBinding = bindingByShapeId.get(fromShapeId);
      const toBinding = bindingByShapeId.get(toShapeId);
      if (!fromBinding || !toBinding) return;

      const managerKey = getPrimaryShapeRecordKey(toShape, toBinding);
      if (!managerKey) return;
      const fromKeys = getShapeRecordKeys(fromShape, fromBinding);
      for (const key of fromKeys) {
        result.set(key, managerKey);
      }
    });

    return result;
  }, [doc, sourceId, getShapeBinding, getPrimaryShapeRecordKey, getShapeRecordKeys]);

  /**
   * Get all shapes with data bindings for the given source
   */
  const getBoundShapes = useCallback((): Array<{ shapeId: string; binding: ShapeDataBinding }> => {
    if (!doc || !sourceId) return [];

    const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));
    const result: Array<{ shapeId: string; binding: ShapeDataBinding }> = [];
    const managerByRecordId = getReportingManagerByRecordId();
    const now = toISODateString(new Date());

    shapesMap.forEach((rawShape, shapeId) => {
      const shape = readYjsValue<Record<string, unknown>>(rawShape);
      const binding = getShapeBinding(shape);

      if (binding && binding.sourceId === sourceId) {
        const state = getBoundRecordState(doc, sourceId, binding.recordId);
        const edgeManagerId = normalizeManagerId(managerByRecordId.get(binding.recordId));
        const stateLocalFields = { ...(state?.localChanges ?? {}) };
        const baselineManagerId = getManagerFieldValue(binding.cachedRecord);
        const localManagerId = getManagerFieldValue(stateLocalFields);
        const effectiveManagerId = localManagerId ?? baselineManagerId;

        if (edgeManagerId && edgeManagerId !== effectiveManagerId) {
          stateLocalFields.reportsTo = edgeManagerId;
        }

        const hasLocalFields = Object.keys(stateLocalFields).length > 0;
        const bindingWithLocalChanges = hasLocalFields
          ? {
              ...binding,
              localChanges: {
                fields: stateLocalFields,
                changedAt: (state?.changedAt as ISODateString | undefined) ?? now,
                changedBy: state?.changedBy ?? 'system',
              },
            }
          : binding;
        const normalizedBinding = {
          ...bindingWithLocalChanges,
          isNewRecord: isNewRecordBinding(bindingWithLocalChanges),
        };

        result.push({ shapeId, binding: normalizedBinding });
      }
    });

    return result;
  }, [
    doc,
    sourceId,
    getShapeBinding,
    getReportingManagerByRecordId,
    getManagerFieldValue,
    normalizeManagerId,
  ]);

  /**
   * Get shapes with uncommitted changes
   */
  const getUncommittedShapes = useCallback((): Array<{ shapeId: string; binding: ShapeDataBinding }> => {
    return getBoundShapes().filter(({ binding }) => hasUncommittedChanges(binding));
  }, [getBoundShapes]);

  /**
   * Get all edges with data bindings (edge-table pattern)
   */
  const getBoundEdges = useCallback((): Array<{ edgeId: string; binding: ShapeDataBinding }> => {
    if (!doc || !sourceId) return [];
    return getBoundEdgesFromDoc(doc, sourceId);
  }, [doc, sourceId]);

  /**
   * Get edges with uncommitted changes (new or modified edge records)
   */
  const getUncommittedEdges = useCallback((): Array<{ edgeId: string; binding: ShapeDataBinding }> => {
    return getBoundEdges().filter(({ binding }) => hasUncommittedChanges(binding));
  }, [getBoundEdges]);

  /**
   * Get edge-table relationship mappings from profile
   */
  const getEdgeTableMappings = useCallback((): EdgeTableRelationshipMapping[] => {
    if (!profile?.relationshipMappings) return [];
    return profile.relationshipMappings.filter(
      (m): m is EdgeTableRelationshipMapping => m.pattern === 'edge-table'
    );
  }, [profile]);

  const applyTransform = useCallback((value: unknown, transform?: FieldTransform): unknown => {
    if (!transform || typeof value !== 'string') {
      return value;
    }

    switch (transform) {
      case 'uppercase':
        return value.toUpperCase();
      case 'lowercase':
        return value.toLowerCase();
      case 'trim':
        return value.trim();
      case 'date_format':
        try {
          return new Date(value).toLocaleDateString();
        } catch {
          return value;
        }
      case 'number_format':
      default:
        return value;
    }
  }, []);

  const applyFieldMappings = useCallback(
    (data: Record<string, unknown>): Record<string, unknown> => {
      const result: Record<string, unknown> = {};

      if (fieldMappings && fieldMappings.length > 0) {
        for (const mapping of fieldMappings) {
          const sourceValue = data[mapping.sourceColumn];
          if (sourceValue !== undefined) {
            result[mapping.targetField] = applyTransform(sourceValue, mapping.transform);
          }
        }
        return result;
      }

      // Fallback when explicit connection mappings are missing:
      // derive mapping from profile aliases so pull can still refresh bound node fields.
      if (profile?.fieldAliases) {
        const detected = detectFieldsFromProfile(Object.keys(data), profile);
        for (const [targetField, sourceColumn] of Object.entries(detected)) {
          if (!sourceColumn) continue;
          const value = data[sourceColumn];
          if (value !== undefined) {
            result[targetField] = value;
          }
        }
      }

      return result;
    },
    [fieldMappings, applyTransform, profile]
  );

  const mapPropsToAttrs = useCallback(
    (mappedProps: Record<string, unknown>): Record<string, unknown> => {
      if (!profile?.propsToAttrsMapping) return {};
      const attrs: Record<string, unknown> = {};
      for (const [externalField, attrName] of Object.entries(profile.propsToAttrsMapping)) {
        if (externalField in mappedProps) {
          attrs[attrName] = mappedProps[externalField];
        }
      }
      return attrs;
    },
    [profile]
  );

  const invalidateRecordsQueries = useCallback(() => {
    if (!sourceId) return;
    queryClient.invalidateQueries({
      // Keep the key param-less so all paginated record queries are invalidated.
      queryKey: [...dataSourceKeys.all, 'records', sourceId],
    });
  }, [queryClient, sourceId]);

  /**
   * Pull latest data from repository and detect conflicts.
   * Fetches fresh records from the external data source and merges with local state.
   *
   * Handles:
   * - Clean update: No local changes → update cachedRecord, mark synced
   * - Non-overlapping changes: Local + remote changed different fields → merge
   * - Conflict: Local + remote changed SAME fields with different values → conflict
   * - Deleted externally: Record not found → mark RECORD_NOT_FOUND error on shape
   * - Renewed: Record replaced externally → detected via field-level comparison
   *
   * New records (isNewRecord) are skipped — they have no external record to compare.
   */
  const pullFromRepository = useCallback(async (): Promise<PullResult> => {
    if (!doc || !sourceId) {
      return { success: false, updated: 0, conflicts: [], deletions: [], errors: [{ shapeId: '', error: 'No document or source' }] };
    }

    setIsPulling(true);
    const result: PullResult = { success: true, updated: 0, conflicts: [], deletions: [], errors: [] };

    try {
      const boundShapes = getBoundShapes();

      if (boundShapes.length === 0) {
        return result;
      }

      // Separate new records (no external record to pull) from existing bindings
      const existingBindings = boundShapes.filter(({ binding }) => !binding.isNewRecord);
      const recordIds = [...new Set(existingBindings.map(({ binding }) => binding.recordId))];

      if (recordIds.length === 0) {
        // Only new records, nothing to pull
        return result;
      }

      // Fetch fresh records from external source
      const freshRecords: Map<string, Record<string, unknown>> = new Map();
      try {
        const response = await authenticatedFetch(`/api/data-sources/${sourceId}/pull`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          cache: 'no-store',
          body: JSON.stringify({ recordIds }),
        });

        if (response.ok) {
          const data = await response.json();
          const records = data.records as Array<{ id: string; data: Record<string, unknown> }>;
          for (const record of records) {
            freshRecords.set(record.id, record.data);
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error ?? 'Pull request failed');
        }
      } catch (fetchError) {
        result.success = false;
        result.errors.push({
          shapeId: '',
          error: fetchError instanceof Error ? fetchError.message : 'Failed to fetch from external source',
        });
        setIsPulling(false);
        return result;
      }

      // Apply updates in a single Yjs transaction
      doc.transact(() => {
        const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));
        const now = toISODateString(new Date());

        const buildUpdatedShape = (
          shape: Record<string, unknown>,
          updatedBinding: ShapeDataBinding,
          propsPatch: Record<string, unknown> = {},
          attrsPatch: Record<string, unknown> = {}
        ) => {
          const hasTopLevelBinding = Boolean(shape.dataBinding);
          const props = (shape.props as Record<string, unknown>) ?? {};
          const attrs = (shape.attrs as Record<string, unknown>) ?? {};
          const nextProps = {
            ...props,
            ...propsPatch,
            dataBinding: updatedBinding,
          };
          const shouldUpdateAttrs = Object.keys(attrsPatch).length > 0 || !!attrs.dataBinding;
          const nextAttrs = shouldUpdateAttrs
            ? {
                ...attrs,
                ...attrsPatch,
                ...(attrs.dataBinding ? { dataBinding: updatedBinding } : {}),
              }
            : attrs;
          return {
            ...shape,
            ...(hasTopLevelBinding ? { dataBinding: updatedBinding } : {}),
            props: nextProps,
            attrs: nextAttrs,
          };
        };

        for (const { shapeId, binding } of existingBindings) {
          const rawShape = shapesMap.get(shapeId);
          const shape = rawShape != null ? readYjsValue<Record<string, unknown>>(rawShape) : undefined;
          if (!shape) continue;

          const freshData = freshRecords.get(binding.recordId);
          if (!freshData) {
            // Record deleted externally — mark shape with RECORD_NOT_FOUND error
            const hadLocal = hasUncommittedChanges(binding);
            result.deletions.push({
              shapeId,
              recordId: binding.recordId,
              hadLocalChanges: hadLocal,
              lastKnownData: binding.cachedRecord ?? {},
            });

            const updatedBinding: ShapeDataBinding = {
              ...binding,
              syncState: {
                status: 'error',
                error: {
                  code: 'RECORD_NOT_FOUND',
                  message: `Record "${binding.recordId}" was deleted from the external source`,
                  retryable: false,
                },
                failedAt: now,
              },
            };

            shapesMap.patch(shapeId, buildUpdatedShape(shape, updatedBinding));
            continue;
          }

          const previousCache = binding.cachedRecord ?? {};

          if (!hasUncommittedChanges(binding)) {
            // No local changes — simply update cache
            const updatedBinding: ShapeDataBinding = {
              ...binding,
              cachedRecord: freshData,
              syncState: { status: 'synced', syncedAt: now },
            };

            const mappedProps = applyFieldMappings(freshData);
            const mappedAttrs = mapPropsToAttrs(mappedProps);
            shapesMap.patch(shapeId, buildUpdatedShape(shape, updatedBinding, mappedProps, mappedAttrs));
            result.updated++;
          } else {
            // Has local changes — check for conflicts
            const localFields = binding.localChanges?.fields as Record<string, unknown> | undefined;
            if (!localFields) {
              result.updated++;
              continue;
            }

            // Find fields that changed remotely (compare fresh vs what we had cached)
            const remoteChanges: Record<string, unknown> = {};
            for (const [key, value] of Object.entries(freshData)) {
              if (previousCache[key] !== value) {
                remoteChanges[key] = value;
              }
            }
            // Also detect fields that were removed remotely
            for (const key of Object.keys(previousCache)) {
              if (!(key in freshData)) {
                remoteChanges[key] = undefined;
              }
            }

            // Check for overlapping changes (same field changed both locally and remotely)
            const conflictingFields: string[] = [];
            for (const field of Object.keys(localFields)) {
              if (field in remoteChanges && remoteChanges[field] !== localFields[field]) {
                conflictingFields.push(field);
              }
            }

            if (conflictingFields.length > 0) {
              // Conflict detected
              const updatedBinding: ShapeDataBinding = {
                ...binding,
                cachedRecord: freshData,
                syncState: {
                  status: 'conflict',
                  conflictData: {
                    localChanges: localFields,
                    remoteChanges,
                    baseRecord: previousCache,
                  },
                  detectedAt: now,
                },
              };

              shapesMap.patch(shapeId, buildUpdatedShape(shape, updatedBinding));

              result.conflicts.push({
                shapeId,
                recordId: binding.recordId,
                localChanges: localFields,
                remoteChanges,
                baseRecord: previousCache,
              });
            } else {
              // No conflict — update cache, preserve local changes
              const updatedBinding: ShapeDataBinding = {
                ...binding,
                cachedRecord: freshData,
                syncState: { status: 'synced', syncedAt: now },
              };

              const mergedData = {
                ...freshData,
                ...localFields,
              };
              const mappedProps = applyFieldMappings(mergedData);
              const mappedAttrs = mapPropsToAttrs(mappedProps);
              shapesMap.patch(shapeId, buildUpdatedShape(shape, updatedBinding, mappedProps, mappedAttrs));
              result.updated++;
            }
          }
        }
      }, 'repository-pull');

      // Edge-table pull: fetch edge records and sync connectors
      const edgeTableMappings = getEdgeTableMappings();
      if (edgeTableMappings.length > 0 && doc) {
        for (const mapping of edgeTableMappings) {
          try {
            const edgeResponse = await authenticatedFetch(`/api/data-sources/${sourceId}/pull`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              cache: 'no-store',
              body: JSON.stringify({ tableId: mapping.tableId }),
            });

            if (edgeResponse.ok) {
              const edgeData = await edgeResponse.json();
              const edgeRecords = edgeData.records as Array<{ id: string; data: Record<string, unknown> }>;
              const connectorsMap = doc.getMap('connectors');
              const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));
              const now = toISODateString(new Date());

              // Build a map of recordId → existing bound edge for update detection
              const existingBoundEdges = getBoundEdges();
              const edgeByRecordId = new Map(
                existingBoundEdges.map(({ edgeId, binding }) => [binding.recordId, { edgeId, binding }])
              );

              // Build a recordId → shape mapping for resolving from/to
              const shapeByRecordId = new Map<string, string>();
              shapesMap.forEach((rawShape, shapeId) => {
                const shape = readYjsValue<Record<string, unknown>>(rawShape);
                const props = shape.props as Record<string, unknown> | undefined;
                const binding = props?.dataBinding as ShapeDataBinding | undefined;
                if (binding?.recordId) {
                  shapeByRecordId.set(binding.recordId, shapeId);
                }
              });

              // Collect new edges to create via MutationManager
              const newEdges: Edge[] = [];

              doc.transact(() => {
                for (const record of edgeRecords) {
                  const fromRecordId = record.data[mapping.sourceField] as string | undefined;
                  const toRecordId = record.data[mapping.targetField] as string | undefined;
                  if (!fromRecordId || !toRecordId) continue;

                  const fromShapeId = shapeByRecordId.get(fromRecordId);
                  const toShapeId = shapeByRecordId.get(toRecordId);
                  if (!fromShapeId || !toShapeId) continue;

                  const existing = edgeByRecordId.get(record.id);
                  if (existing) {
                    // Update existing bound edge's cached record (stays in Yjs transaction)
                    const edge = connectorsMap.get(existing.edgeId) as Record<string, unknown> | undefined;
                    if (edge) {
                      const attrs = (edge.attrs as Record<string, unknown>) ?? {};
                      const updatedBinding: ShapeDataBinding = {
                        ...existing.binding,
                        cachedRecord: record.data,
                        syncState: { status: 'synced', syncedAt: now },
                      };
                      connectorsMap.set(existing.edgeId, {
                        ...edge,
                        from: fromShapeId,
                        to: toShapeId,
                        attrs: { ...attrs, dataBinding: updatedBinding },
                      });
                    }
                    result.updated++;
                  } else {
                    // Collect new edge for MutationManager creation
                    const edgeId = `bound-edge-${record.id}-${Date.now()}`;
                    const edgeBinding: ShapeDataBinding = {
                      sourceId: sourceId!,
                      recordId: record.id,
                      cachedRecord: record.data,
                      syncState: { status: 'synced', syncedAt: now },
                      localVersion: 1,
                      lastWrittenVersion: 1,
                    };
                    newEdges.push({
                      id: edgeId,
                      type: mapping.edgeType as Edge['type'],
                      from: fromShapeId,
                      to: toShapeId,
                      attrs: { dataBinding: edgeBinding },
                    } as Edge);
                    result.updated++;
                  }
                }
              }, 'repository-pull-edges');

              // Create new edges via MutationManager (single point of mutation)
              if (newEdges.length > 0) {
                const edgeMutationManager = getNodeMutationManager();
                edgeMutationManager.batchAddEdges(newEdges);
              }
            }
          } catch (edgePullError) {
            result.errors.push({
              shapeId: '',
              error: `Edge table pull failed for ${mapping.tableId}: ${edgePullError instanceof Error ? edgePullError.message : 'Unknown error'}`,
            });
          }
        }
      }

      result.success = result.conflicts.length === 0 && result.deletions.length === 0;
      setLastPullResult(result);

      if (result.conflicts.length > 0) {
        onConflictsDetected?.(result.conflicts);
      } else if (result.deletions.length === 0) {
        onPullSuccess?.(result);
      }

      // Invalidate queries to refresh UI
      invalidateRecordsQueries();
    } catch (error) {
      result.success = false;
      result.errors.push({
        shapeId: '',
        error: error instanceof Error ? error.message : 'Pull failed',
      });
    } finally {
      setIsPulling(false);
    }

    return result;
  }, [
    doc,
    sourceId,
    getBoundShapes,
    getBoundEdges,
    getShapeBinding,
    getEdgeTableMappings,
    applyFieldMappings,
    mapPropsToAttrs,
    queryClient,
    invalidateRecordsQueries,
    onPullSuccess,
    onConflictsDetected,
    authenticatedFetch,
  ]);

  /**
   * Commit local changes to the repository.
   *
   * Performs a pre-commit pull to detect conflicts before pushing changes.
   * If conflicts or deletions are found, the commit is blocked and shapes
   * are marked in Yjs. The consumer should resolve via resolveConflict()
   * or resolveDeletedRecord() then retry.
   */
  const commitChanges = useCallback(
    async (shapeIds: string[] | 'all' = 'all'): Promise<CommitResult> => {
      if (!doc || !sourceId) {
        return { success: false, committed: 0, failed: 0, conflicts: [], deletions: [], errors: [{ shapeId: '', error: 'No document or source' }] };
      }

      setIsCommitting(true);
      const result: CommitResult = { success: true, committed: 0, failed: 0, conflicts: [], deletions: [], errors: [] };

      try {
        const uncommitted = getUncommittedShapes();
        const deletedNodes = getDeletedNodeBindings(doc, sourceId);
        const toCommit = shapeIds === 'all'
          ? uncommitted
          : uncommitted.filter(({ shapeId }) => shapeIds.includes(shapeId));

        if (toCommit.length === 0 && deletedNodes.length === 0) {
          return result;
        }

        // Pre-commit pull: fetch fresh data and detect conflicts/deletions before committing.
        // New records (isNewRecord) are automatically skipped by pullFromRepository.
        const pullResult = await pullFromRepository();

        if (!pullResult.success && pullResult.errors.length > 0
            && pullResult.conflicts.length === 0 && pullResult.deletions.length === 0) {
          // Pull failed (network error, etc.) — block commit
          result.success = false;
          result.errors.push({
            shapeId: '',
            error: `Pre-commit pull failed: ${pullResult.errors[0]?.error ?? 'Unknown error'}`,
          });
          return result;
        }

        if (pullResult.conflicts.length > 0) {
          // Conflicts detected — block commit, shapes already marked in Yjs by pullFromRepository
          result.success = false;
          result.conflicts = pullResult.conflicts;
          result.deletions = pullResult.deletions;
          setLastCommitResult(result);
          return result;
        }

        if (pullResult.deletions.length > 0) {
          // Records deleted externally — block commit for affected shapes
          result.success = false;
          result.deletions = pullResult.deletions;
          setLastCommitResult(result);
          return result;
        }

        // Re-read uncommitted shapes after pull (pull may have updated cachedRecord)
        const freshUncommitted = getUncommittedShapes();
        const freshToCommit = shapeIds === 'all'
          ? freshUncommitted
          : freshUncommitted.filter(({ shapeId }) => shapeIds.includes(shapeId));
        const freshDeletedNodes = getDeletedNodeBindings(doc, sourceId);

        if (freshToCommit.length === 0 && freshDeletedNodes.length === 0) {
          // All changes were resolved by the pull (remote already has latest)
          return result;
        }

        // Prepare batch commit payload
        const creates: Array<{ tempId: string; data: Record<string, unknown> }> = [];
        const updatesByRecordId = new Map<string, Record<string, unknown>>();
        const deletes: string[] = [];

        const resolveMappedValue = (
          data: Record<string, unknown>,
          targetField: string,
          sourceColumn: string
        ): { found: boolean; value: unknown } => {
          const aliasCandidates = profile?.fieldAliases?.[targetField] ?? [];
          const candidates = [targetField, ...aliasCandidates, sourceColumn];

          for (const key of candidates) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
              return { found: true, value: data[key] };
            }
          }

          const lowerKeyMap = new Map<string, string>();
          Object.keys(data).forEach((key) => {
            lowerKeyMap.set(key.toLowerCase(), key);
          });
          for (const key of candidates) {
            const matchedKey = lowerKeyMap.get(key.toLowerCase());
            if (matchedKey !== undefined) {
              return { found: true, value: data[matchedKey] };
            }
          }

          return { found: false, value: undefined };
        };

        const mapToExternal = (
          data: Record<string, unknown>,
          options?: { includeAllMappedFields?: boolean }
        ) => {
          if (fieldMappings.length > 0) {
            const mapped: Record<string, unknown> = {};
            for (const mapping of fieldMappings) {
              const resolved = resolveMappedValue(
                data,
                mapping.targetField,
                mapping.sourceColumn
              );
              if (resolved.found) {
                mapped[mapping.sourceColumn] = resolved.value;
              } else if (options?.includeAllMappedFields) {
                mapped[mapping.sourceColumn] = null;
              }
            }
            return mapped;
          }
          return profile ? reverseFieldMapping(data, profile) : data;
        };

        for (const { shapeId, binding } of freshToCommit) {
          // Skip shapes with error status (e.g., RECORD_NOT_FOUND from pull)
          if (binding.syncState.status === 'error' || binding.syncState.status === 'conflict') {
            continue;
          }

          const effectiveData = getEffectiveRecord(binding);
          const mappedEffective = mapToExternal(effectiveData, { includeAllMappedFields: true });
          if (binding.isNewRecord) {
            creates.push({ tempId: shapeId, data: mappedEffective });
          } else if (binding.localChanges?.fields) {
            const mappedUpdate = mapToExternal(
              binding.localChanges.fields as Record<string, unknown>
            );
            const existingUpdate = updatesByRecordId.get(binding.recordId);
            updatesByRecordId.set(binding.recordId, {
              ...(existingUpdate ?? {}),
              ...mappedUpdate,
            });
          }
        }

        const updates: Array<{ recordId: string; data: Record<string, unknown> }> = Array.from(
          updatesByRecordId.entries()
        ).map(([recordId, data]) => ({ recordId, data }));

        for (const { binding } of freshDeletedNodes) {
          if (!binding.recordId || binding.isNewRecord) continue;
          deletes.push(binding.recordId);
        }

        const uniqueDeletes = [...new Set(deletes)];

        // Call API to commit changes
        let apiResult: {
          created: Array<{ tempId: string; record: { id: string; data: Record<string, unknown> } }>;
          updated: Array<{ recordId: string; success: boolean; error?: string }>;
          deleted: Array<{ recordId: string; success: boolean; error?: string }>;
        } | null = null;

        if (creates.length > 0 || updates.length > 0 || uniqueDeletes.length > 0) {
          try {
            if (!folderId || !sourceType || !domain) {
              throw new Error('Primary source authority context is required for write-back');
            }
            const response = await authenticatedFetch(
              `/api/folders/${encodeURIComponent(folderId)}/change-sets/writeback`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                sourceType,
                domain,
                diagramId: options.diagramId,
                title: 'Repository record changes',
                creates,
                updates,
                deletes: uniqueDeletes,
              }),
            });

            if (response.ok) {
              const data = await response.json();
              apiResult = data.run?.result?.batch;
            } else {
              const errorData = await response.json().catch(() => ({}));
              result.success = false;
              result.failed += creates.length + updates.length + uniqueDeletes.length;
              result.errors.push({
                shapeId: '',
                error: `Record commit failed: ${(errorData as Record<string, unknown>).error ?? 'Unknown error'}`,
              });
              setLastCommitResult(result);
              return result;
            }
          } catch (apiError) {
            result.success = false;
            result.failed += creates.length + updates.length + uniqueDeletes.length;
            result.errors.push({
              shapeId: '',
              error: `Record commit API call failed: ${apiError instanceof Error ? apiError.message : 'Unknown error'}`,
            });
            setLastCommitResult(result);
            return result;
          }
        }

        // Build commit result maps to prevent marking failed operations as committed.
        const newRecordIdMap = new Map<string, string>();
        const createFailureByTempId = new Map<string, string>();
        if (apiResult?.created) {
          for (const created of apiResult.created) {
            if (created.record.id) {
              newRecordIdMap.set(created.tempId, created.record.id);
            } else {
              createFailureByTempId.set(created.tempId, 'Create failed');
            }
          }
        }
        const updateSuccessByRecordId = new Map<string, boolean>();
        const updateFailureByRecordId = new Map<string, string>();
        if (apiResult?.updated) {
          for (const updated of apiResult.updated) {
            updateSuccessByRecordId.set(updated.recordId, updated.success);
            if (!updated.success) {
              updateFailureByRecordId.set(updated.recordId, updated.error ?? `Failed to update record ${updated.recordId}`);
            }
          }
        }
        const deletedNodeIdsToClear = new Set<string>();
        if (apiResult?.deleted) {
          const successByRecordId = new Map<string, boolean>();
          for (const deleted of apiResult.deleted) {
            successByRecordId.set(deleted.recordId, deleted.success);
            if (!deleted.success) {
              result.failed++;
              result.errors.push({
                shapeId: '',
                error: deleted.error ?? `Failed to delete record ${deleted.recordId}`,
              });
            }
          }

          for (const { nodeId, binding } of freshDeletedNodes) {
            if (successByRecordId.get(binding.recordId) === true) {
              deletedNodeIdsToClear.add(nodeId);
              result.committed++;
            }
          }
        }

        // Update Yjs state
        const committedRecordIds = new Set<string>();
        const addCommittedRecordId = (candidate: unknown) => {
          if (typeof candidate !== 'string') return;
          const normalized = candidate.trim();
          if (normalized.length === 0) return;
          committedRecordIds.add(normalized);
        };
        doc.transact(() => {
          const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));

          for (const { shapeId, binding } of freshToCommit) {
            // Skip shapes with error/conflict status
            if (binding.syncState.status === 'error' || binding.syncState.status === 'conflict') {
              continue;
            }

            try {
              if (binding.isNewRecord) {
                if (!newRecordIdMap.has(shapeId)) {
                  result.failed++;
                  result.errors.push({
                    shapeId,
                    error: createFailureByTempId.get(shapeId) ?? `Failed to create record for shape ${shapeId}`,
                  });
                  continue;
                }
              } else if (binding.localChanges?.fields) {
                const updateSucceeded = updateSuccessByRecordId.get(binding.recordId);
                if (updateSucceeded === false || updateSucceeded === undefined) {
                  result.failed++;
                  result.errors.push({
                    shapeId,
                    error: updateFailureByRecordId.get(binding.recordId) ?? `Failed to update record ${binding.recordId}`,
                  });
                  continue;
                }
              }

              const rawShape = shapesMap.get(shapeId);
              const shape = rawShape != null ? readYjsValue<Record<string, unknown>>(rawShape) : undefined;
              if (!shape) {
                result.errors.push({ shapeId, error: 'Shape not found' });
                result.failed++;
                continue;
              }

              // Get the effective record (cachedRecord + localChanges)
              const committedData = getEffectiveRecord(binding);

              // For new records, get permanent ID from API response or generate locally
              const permanentRecordId = binding.isNewRecord
                ? newRecordIdMap.get(shapeId) ?? `PROC-${Date.now().toString(36).toUpperCase()}`
                : binding.recordId;

              // Mark as committed
              const updatedBinding = markBindingCommitted(
                binding,
                committedData,
                binding.isNewRecord ? permanentRecordId : undefined
              );

              // Update shape in Yjs
              const props = (shape.props as Record<string, unknown>) ?? {};
              const attrs = (shape.attrs as Record<string, unknown>) ?? {};
              const hasTopLevelBinding = Boolean(shape.dataBinding);
              const hasAttrsBinding = Boolean(attrs.dataBinding);
              shapesMap.patch(shapeId, {
                ...shape,
                ...(hasTopLevelBinding ? { dataBinding: updatedBinding } : {}),
                props: {
                  ...props,
                  dataBinding: updatedBinding,
                },
                ...(hasAttrsBinding
                  ? {
                      attrs: {
                        ...attrs,
                        dataBinding: updatedBinding,
                      },
                    }
                  : {}),
              });
              addCommittedRecordId(binding.recordId);
              if (binding.isNewRecord && permanentRecordId !== binding.recordId) {
                addCommittedRecordId(permanentRecordId);
              }
              const topLevelBinding = shape.dataBinding as ShapeDataBinding | undefined;
              addCommittedRecordId(topLevelBinding?.recordId);
              addCommittedRecordId((props.personId ?? undefined));
              addCommittedRecordId((props.id ?? undefined));
              addCommittedRecordId((attrs.personId ?? undefined));
              addCommittedRecordId((attrs.id ?? undefined));

              result.committed++;
            } catch (error) {
              result.errors.push({
                shapeId,
                error: error instanceof Error ? error.message : 'Unknown error',
              });
              result.failed++;
            }
          }
        }, 'repository-commit');

        if (committedRecordIds.size > 0) {
          markRecordsAsCommitted(doc, sourceId, Array.from(committedRecordIds));
        }
        if (deletedNodeIdsToClear.size > 0) {
          doc.transact(() => {
            clearDeletedNodeBindings(doc, Array.from(deletedNodeIdsToClear));
          }, 'repository-commit-clear-node-deletes');
        }

        // Edge-table commit: push new/updated/deleted edge records
        const edgeTableMappings = getEdgeTableMappings();
        if (edgeTableMappings.length > 0) {
          const uncommittedEdges = getUncommittedEdges();
          const deletedEdges = getDeletedEdgeBindings(doc, sourceId);

          const edgeCreates: Array<{ tempId: string; data: Record<string, unknown>; tableId: string }> = [];
          const edgeUpdates: Array<{ recordId: string; data: Record<string, unknown>; tableId: string }> = [];
          const edgeDeletes: Array<{ edgeId: string; recordId: string; tableId: string }> = [];
          const edgeTableById = new Map<string, string>();

          for (const { edgeId, binding } of uncommittedEdges) {
            // Find the matching edge-table mapping for this binding's edge type
            const connectorsMap = doc.getMap('connectors');
            const edge = connectorsMap.get(edgeId) as Record<string, unknown> | undefined;
            const edgeType = edge?.type as string | undefined;
            const mapping = edgeTableMappings.find(m => m.edgeType === edgeType);
            if (!mapping) continue;

            if (binding.isNewRecord) {
              edgeCreates.push({
                tempId: edgeId,
                data: getEffectiveRecord(binding),
                tableId: mapping.tableId,
              });
              edgeTableById.set(edgeId, mapping.tableId);
            } else if (binding.localChanges?.fields) {
              edgeUpdates.push({
                recordId: binding.recordId,
                data: binding.localChanges.fields as Record<string, unknown>,
                tableId: mapping.tableId,
              });
              edgeTableById.set(edgeId, mapping.tableId);
            }
          }

          // Collect deleted edge records grouped by table
          for (const { edgeId, binding } of deletedEdges) {
            const cachedData = binding.cachedRecord as Record<string, unknown> | undefined;
            const matchedMapping = edgeTableMappings.find(m =>
              cachedData && m.sourceField in cachedData
            );
            // Fall back to first mapping if we can't determine the table
            const tableId = matchedMapping?.tableId ?? edgeTableMappings[0]?.tableId;
            if (tableId) {
              edgeDeletes.push({ edgeId, recordId: binding.recordId, tableId });
            }
          }

          if (edgeCreates.length > 0 || edgeUpdates.length > 0 || edgeDeletes.length > 0) {
            const edgeCreateIdMap = new Map<string, string>();
            const edgeCreateFailureById = new Map<string, string>();
            const edgeUpdateSuccessByKey = new Map<string, boolean>();
            const edgeUpdateFailureByKey = new Map<string, string>();
            const successfulDeletedEdgeIds = new Set<string>();

            const edgeGroups = new Map<
              string,
              {
                creates: Array<{ tempId: string; data: Record<string, unknown> }>;
                updates: Array<{ recordId: string; data: Record<string, unknown> }>;
                deletes: Array<{ edgeId: string; recordId: string }>;
              }
            >();
            const getEdgeGroup = (tableId: string) => {
              let group = edgeGroups.get(tableId);
              if (!group) {
                group = { creates: [], updates: [], deletes: [] };
                edgeGroups.set(tableId, group);
              }
              return group;
            };

            for (const create of edgeCreates) {
              getEdgeGroup(create.tableId).creates.push({ tempId: create.tempId, data: create.data });
            }
            for (const update of edgeUpdates) {
              getEdgeGroup(update.tableId).updates.push({ recordId: update.recordId, data: update.data });
            }
            for (const del of edgeDeletes) {
              getEdgeGroup(del.tableId).deletes.push({ edgeId: del.edgeId, recordId: del.recordId });
            }

            try {
              for (const [tableId, group] of edgeGroups.entries()) {
                if (!folderId || !sourceType || !domain) {
                  throw new Error('Primary source authority context is required for relationship write-back');
                }
                const edgeResponse = await authenticatedFetch(
                  `/api/folders/${encodeURIComponent(folderId)}/change-sets/writeback`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    sourceType,
                    domain,
                    diagramId: options.diagramId,
                    title: `Repository relationship changes (${tableId})`,
                    tableId,
                    creates: group.creates,
                    updates: group.updates,
                    deletes: group.deletes.map((d) => ({ recordId: d.recordId, tableId })),
                  }),
                });

                if (!edgeResponse.ok) {
                  const errorData = await edgeResponse.json().catch(() => ({}));
                  result.failed += group.creates.length + group.updates.length + group.deletes.length;
                  result.errors.push({
                    shapeId: '',
                    error: `Edge record commit failed for ${tableId}: ${(errorData as Record<string, unknown>).error ?? 'Unknown error'}`,
                  });
                  continue;
                }

                const edgeApiResult = await edgeResponse.json();
                const edgeResults = edgeApiResult.run?.result?.batch as {
                  created?: Array<{ tempId: string; record: { id: string; data: Record<string, unknown> } }>;
                  updated?: Array<{ recordId: string; success: boolean; error?: string }>;
                  deleted?: Array<{ recordId: string; success: boolean; error?: string }>;
                };

                const deleteEdgeByRecordId = new Map(group.deletes.map((entry) => [entry.recordId, entry.edgeId]));

                for (const created of edgeResults.created ?? []) {
                  if (created.record.id) {
                    edgeCreateIdMap.set(created.tempId, created.record.id);
                  } else {
                    edgeCreateFailureById.set(created.tempId, 'Create failed');
                    result.failed++;
                  }
                }

                for (const updated of edgeResults.updated ?? []) {
                  const key = `${tableId}:${updated.recordId}`;
                  edgeUpdateSuccessByKey.set(key, updated.success);
                  if (!updated.success) {
                    edgeUpdateFailureByKey.set(
                      key,
                      updated.error ?? `Failed to update edge record ${updated.recordId}`
                    );
                    result.failed++;
                  }
                }

                for (const deleted of edgeResults.deleted ?? []) {
                  if (deleted.success) {
                    const deletedEdgeId = deleteEdgeByRecordId.get(deleted.recordId);
                    if (deletedEdgeId) {
                      successfulDeletedEdgeIds.add(deletedEdgeId);
                    }
                  } else {
                    result.failed++;
                    result.errors.push({
                      shapeId: '',
                      error: deleted.error ?? `Failed to delete edge record ${deleted.recordId}`,
                    });
                  }
                }
              }

              doc.transact(() => {
                const connectorsMap = doc.getMap('connectors');

                for (const { edgeId, binding } of uncommittedEdges) {
                  const tableId = edgeTableById.get(edgeId);
                  if (!tableId) continue;

                  const edge = connectorsMap.get(edgeId) as Record<string, unknown> | undefined;
                  if (!edge) continue;

                  if (binding.isNewRecord) {
                    if (!edgeCreateIdMap.has(edgeId)) {
                      result.errors.push({
                        shapeId: edgeId,
                        error: edgeCreateFailureById.get(edgeId) ?? `Failed to create edge record for ${edgeId}`,
                      });
                      continue;
                    }
                  } else if (binding.localChanges?.fields) {
                    const updateKey = `${tableId}:${binding.recordId}`;
                    const updateSucceeded = edgeUpdateSuccessByKey.get(updateKey);
                    if (updateSucceeded === false || updateSucceeded === undefined) {
                      result.errors.push({
                        shapeId: edgeId,
                        error: edgeUpdateFailureByKey.get(updateKey) ?? `Failed to update edge record ${binding.recordId}`,
                      });
                      continue;
                    }
                  }

                  const committedData = getEffectiveRecord(binding);
                  const permanentId = binding.isNewRecord
                    ? edgeCreateIdMap.get(edgeId) ?? binding.recordId
                    : binding.recordId;

                  const updatedBinding = markBindingCommitted(
                    binding,
                    committedData,
                    binding.isNewRecord ? permanentId : undefined
                  );

                  const attrs = (edge.attrs as Record<string, unknown>) ?? {};
                  connectorsMap.set(edgeId, {
                    ...edge,
                    attrs: { ...attrs, dataBinding: updatedBinding },
                  });
                  result.committed++;
                }
              }, 'repository-commit-edges');

              if (successfulDeletedEdgeIds.size > 0) {
                clearDeletedEdgeBindings(doc, sourceId, Array.from(successfulDeletedEdgeIds));
                result.committed += successfulDeletedEdgeIds.size;
              }
            } catch (edgeCommitError) {
              result.failed += edgeCreates.length + edgeUpdates.length + edgeDeletes.length;
              result.errors.push({
                shapeId: '',
                error: `Edge commit failed: ${edgeCommitError instanceof Error ? edgeCommitError.message : 'Unknown error'}`,
              });
            }
          }
        }

        result.success = result.failed === 0 && result.conflicts.length === 0 && result.deletions.length === 0;
        setLastCommitResult(result);

        if (result.success) {
          // Invalidate queries to refresh UI
          invalidateRecordsQueries();
          onCommitSuccess?.(result);
        }
      } catch (error) {
        result.success = false;
        result.errors.push({
          shapeId: '',
          error: error instanceof Error ? error.message : 'Commit failed',
        });
      } finally {
        setIsCommitting(false);
      }

      return result;
    },
    [
      doc,
      sourceId,
      getUncommittedShapes,
      getUncommittedEdges,
      getEdgeTableMappings,
      pullFromRepository,
      invalidateRecordsQueries,
      onCommitSuccess,
      profile,
      authenticatedFetch,
      fieldMappings,
      folderId,
      sourceType,
      domain,
      options.diagramId,
    ]
  );

  /**
   * Discard local changes and revert to committed state
   */
  const discardChanges = useCallback(
    async (shapeIds: string[] | 'all' = 'all'): Promise<DiscardResult> => {
      if (!doc || !sourceId) {
        return { discarded: 0, errors: ['No document or source'] };
      }

      setIsDiscarding(true);
      const result: DiscardResult = { discarded: 0, errors: [] };

      try {
        const uncommitted = getUncommittedShapes();
        const deletedNodes = getDeletedNodeBindings(doc, sourceId);
        const toDiscard = shapeIds === 'all'
          ? uncommitted
          : uncommitted.filter(({ shapeId }) => shapeIds.includes(shapeId));

        if (toDiscard.length === 0 && deletedNodes.length === 0) {
          return result;
        }

        doc.transact(() => {
          const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));
          const discardedRecordIds = new Set<string>();

          for (const { shapeId, binding } of toDiscard) {
            try {
              discardedRecordIds.add(binding.recordId);
              if (binding.isNewRecord) {
                // For new records, delete the shape entirely
                shapesMap.delete(shapeId);
                result.discarded++;
              } else {
                // For existing records, clear local changes
                const rawShape = shapesMap.get(shapeId);
                const shape = rawShape != null ? readYjsValue<Record<string, unknown>>(rawShape) : undefined;
                if (shape) {
                  const updatedBinding = discardLocalChanges(binding);
                  const mappedProps = applyFieldMappings(updatedBinding.cachedRecord);
                  const mappedAttrs = mapPropsToAttrs(mappedProps);
                  const props = (shape.props as Record<string, unknown>) ?? {};
                  const attrs = (shape.attrs as Record<string, unknown>) ?? {};
                  const hasTopLevelBinding = Boolean(shape.dataBinding);
                  shapesMap.patch(shapeId, {
                    ...shape,
                    ...(hasTopLevelBinding ? { dataBinding: updatedBinding } : {}),
                    props: {
                      ...props,
                      ...mappedProps,
                      dataBinding: updatedBinding,
                    },
                    attrs: {
                      ...attrs,
                      ...mappedAttrs,
                      ...(attrs.dataBinding ? { dataBinding: updatedBinding } : {}),
                    },
                  });
                  result.discarded++;
                }
              }
            } catch (error) {
              result.errors.push(
                `Failed to discard ${shapeId}: ${error instanceof Error ? error.message : 'Unknown error'}`
              );
            }
          }

          if (deletedNodes.length > 0) {
            clearDeletedNodeBindings(
              doc,
              deletedNodes.map((entry) => entry.nodeId)
            );
          }

          // Keep centralized uncommitted state in sync with discarded bindings.
          if (shapeIds === 'all') {
            discardAllBoundRecordChanges(doc, sourceId);
          } else if (discardedRecordIds.size > 0) {
            const boundRecordStateMap = doc.getMap('boundRecordState');
            for (const recordId of discardedRecordIds) {
              boundRecordStateMap.delete(`${sourceId}:${recordId}`);
            }
          }
        }, 'repository-discard');
      } catch (error) {
        result.errors.push(
          `Discard operation failed: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      } finally {
        setIsDiscarding(false);
      }

      return result;
    },
    [doc, sourceId, getUncommittedShapes, applyFieldMappings, mapPropsToAttrs]
  );

  /**
   * Resolve a conflict on a specific shape.
   * After resolving, the consumer can retry commitChanges().
   */
  const resolveConflict = useCallback(
    async (
      shapeId: string,
      resolution: 'local' | 'remote' | 'merge',
      mergedData?: Record<string, unknown>
    ): Promise<{ success: boolean; error?: string }> => {
      if (!doc) {
        return { success: false, error: 'No document' };
      }

      try {
        const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));
        const rawShape = shapesMap.get(shapeId);
        const shape = rawShape != null ? readYjsValue<Record<string, unknown>>(rawShape) : undefined;

        if (!shape) {
          return { success: false, error: 'Shape not found' };
        }

        const props = shape.props as Record<string, unknown> | undefined;
        const binding = getShapeBinding(shape);

        if (!binding || binding.syncState.status !== 'conflict') {
          return { success: false, error: 'Shape has no conflict to resolve' };
        }

        doc.transact(() => {
          const attrs = (shape.attrs as Record<string, unknown>) ?? {};
          const hasTopLevelBinding = Boolean(shape.dataBinding);
          const hasAttrsBinding = Boolean(attrs.dataBinding);
          let resolvedBinding: ShapeDataBinding;
          const now = toISODateString(new Date());

          if (resolution === 'local') {
            // Keep local changes as-is, cachedRecord already has fresh data from pull
            resolvedBinding = {
              ...binding,
              syncState: { status: 'synced', syncedAt: now },
            };
          } else if (resolution === 'remote') {
            // Discard local changes, use remote data (cachedRecord already has fresh data from pull)
            resolvedBinding = discardLocalChanges(binding);
          } else {
            // Merge: use provided merged data as new local changes
            resolvedBinding = {
              ...binding,
              localChanges: mergedData
                ? {
                    fields: mergedData,
                    changedAt: now,
                    changedBy: binding.localChanges?.changedBy ?? 'unknown',
                  }
                : undefined,
              syncState: { status: 'synced', syncedAt: now },
            };
          }

          shapesMap.patch(shapeId, {
            ...shape,
            ...(hasTopLevelBinding ? { dataBinding: resolvedBinding } : {}),
            props: {
              ...props,
              dataBinding: resolvedBinding,
            },
            ...(hasAttrsBinding
              ? {
                  attrs: {
                    ...attrs,
                    dataBinding: resolvedBinding,
                  },
                }
              : {}),
          });
        }, 'resolve-conflict');

        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [doc, getShapeBinding]
  );

  /**
   * Resolve a deleted-externally record on a specific shape.
   *
   * Options:
   * - 'unbind': Remove the data binding from the shape (shape becomes standalone)
   * - 'recreate': Convert to a new record (will be created on next commit)
   * - 'delete': Delete the shape entirely from the diagram
   */
  const resolveDeletedRecord = useCallback(
    async (
      shapeId: string,
      resolution: 'unbind' | 'recreate' | 'delete'
    ): Promise<{ success: boolean; error?: string }> => {
      if (!doc || !sourceId) {
        return { success: false, error: 'No document or source' };
      }

      try {
        const shapesMap = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));
        const rawShape = shapesMap.get(shapeId);
        const shape = rawShape != null ? readYjsValue<Record<string, unknown>>(rawShape) : undefined;

        if (!shape) {
          return { success: false, error: 'Shape not found' };
        }

        const props = shape.props as Record<string, unknown> | undefined;
        const binding = getShapeBinding(shape);

        if (!binding) {
          return { success: false, error: 'Shape has no data binding' };
        }

        // Verify the shape actually has a RECORD_NOT_FOUND error
        if (binding.syncState.status !== 'error'
            || binding.syncState.error.code !== 'RECORD_NOT_FOUND') {
          return { success: false, error: 'Shape does not have a deleted record error' };
        }

        doc.transact(() => {
          if (resolution === 'delete') {
            // Delete the entire shape from the diagram
            shapesMap.delete(shapeId);
          } else if (resolution === 'unbind') {
            // Remove data binding, keep the shape as standalone
            shapesMap.patch(shapeId, buildShapeUnbindPatch(shape));
          } else {
            // 'recreate': Convert to a new record with the last known data as initial fields
            const now = toISODateString(new Date());
            const effectiveData = binding.localChanges?.fields
              ? { ...binding.cachedRecord, ...binding.localChanges.fields }
              : { ...binding.cachedRecord };

            const newBinding: ShapeDataBinding = {
              sourceId: binding.sourceId,
              recordId: `NEW-${shapeId}`,
              cachedRecord: {},
              syncState: { status: 'synced', syncedAt: now },
              localChanges: {
                fields: effectiveData,
                changedAt: now,
                changedBy: binding.localChanges?.changedBy ?? 'system',
              },
              isNewRecord: true,
              localVersion: 1,
              lastWrittenVersion: 0,
            };
            const attrs = (shape.attrs as Record<string, unknown>) ?? {};
            const hasTopLevelBinding = Boolean(shape.dataBinding);
            const hasAttrsBinding = Boolean(attrs.dataBinding);

            shapesMap.patch(shapeId, {
              ...shape,
              ...(hasTopLevelBinding ? { dataBinding: newBinding } : {}),
              props: {
                ...props,
                dataBinding: newBinding,
              },
              ...(hasAttrsBinding
                ? {
                    attrs: {
                      ...attrs,
                      dataBinding: newBinding,
                    },
                  }
                : {}),
            });
          }
        }, 'resolve-deleted-record');

        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [doc, sourceId, getShapeBinding]
  );

  return {
    // Operations
    commitChanges,
    pullFromRepository,
    discardChanges,
    resolveConflict,
    resolveDeletedRecord,

    // State
    isCommitting,
    isPulling,
    isDiscarding,
    lastCommitResult,
    lastPullResult,

    // Helpers
    getBoundShapes,
    getUncommittedShapes,
  };
}
